import React, { useState } from 'react';
import { Colors, Text } from 'library';
import { Stack } from 'library/ui';
import { styled, Rating } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { isAfter } from 'date-fns';
import { formatMonthYearWith12HourTime } from 'shared/Dates';
import { useHistory } from 'react-router';

import ProviderReviewModal from 'parent-portal/home/providerReivewModal/ProviderReviewModal';
import { BasicCard } from './BasicCard';
import { WorkerTab } from './WorkerTab';

import type { TProviderRecentlyWorked } from 'parent-portal/home/new/types';

type IAppointment = TProviderRecentlyWorked['appointments'][0];

export function Reviews({
    loading,
    small,
    workers,
}: {
    loading: boolean;
    small: boolean;
    workers?: TProviderRecentlyWorked[];
}) {
    const history = useHistory();
    const [open, setOpen] = useState<boolean>(false);

    function lastWorked(appointments: IAppointment[]) {
        const now = new Date();

        const lastWorkedAppoinment = appointments.reduce((acc: IAppointment, curr: IAppointment) => {
            const date = new Date(curr.start_date);
            return !isAfter(date, now) && isAfter(new Date(acc.start_date), date) ? curr : acc;
        }, appointments[0]);

        return lastWorkedAppoinment
            ? formatMonthYearWith12HourTime(lastWorkedAppoinment.start_date, lastWorkedAppoinment.timezone)
            : 'Unknown';
    }

    function onClick(userId: number, value?: number | null) {
        if (value) {
            history.push(`/providers/review/${userId}?initialValue=${value}`);
            return;
        }

        history.push(`/providers/review/${userId}`);
    }

    if (!workers || workers.length === 0) return null;

    return (
        <BasicCard loading={loading} small={small}>
            <Row>
                <Text variant="h1">Reviews</Text>
                <ViewAll onClick={() => setOpen(true)}>View All</ViewAll>
            </Row>
            <Row>
                <Text variant="body2">
                    Your reviews help us match you with the best-fit workers and give private, anonymized feedback that
                    helps workers improve. We never share individual reviews—your input stays confidential and helps
                    raise the bar for care.
                </Text>
            </Row>
            <Stack gap="sm" align="start">
                {workers.map((worker: TProviderRecentlyWorked) => (
                    <WorkerTab
                        key={worker.id}
                        worker={{
                            user: worker.user,
                            profile_image: worker.profile_image,
                            middle: (
                                <>
                                    <b>Last Worked:</b> {lastWorked(worker.appointments)}
                                    <br />
                                    <Rating onChange={(_, value) => onClick(worker.user.id, value)} />
                                </>
                            ),
                        }}
                        small={small}
                        onClick={() => onClick(worker.user.id)}
                        icon={ArrowForward}
                    />
                ))}
            </Stack>
            <ProviderReviewModal workers={workers} open={open} onCloseModal={() => setOpen(false)} />
        </BasicCard>
    );
}

const Row = styled('div')({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 20,
    justifyContent: 'space-between',
    width: '100%',
});

const ViewAll = styled('div')({
    textDecorationLine: 'underline',
    cursor: 'pointer',
    fontSize: 14,
    fontFamily: 'Outfit',
    color: Colors.darkNavy,
    '&:hover': {
        color: Colors.mediumGrey,
    },
});
